import { FaClock, FaMapMarkerAlt, FaDollarSign, FaCheckCircle, FaCreditCard, FaHourglass, FaBan, FaTimesCircle, FaTools, FaCheckDouble } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import './RequestCard.css'

interface RequestCardProps {
    request: {
        id: number
        type?: 'service_request' | 'quote_request'
        service_name: string
        description?: string
        status: string
        estimated_price?: number
        scheduled_date?: string
        scheduled_hour?: number
        estimated_duration_hours?: number
        location_details?: {
            address?: string
            city?: string
        }
        created_at: string
    }
}

const getStatusInfo = (status: string, type?: 'service_request' | 'quote_request') => {
    // Quote request statuses
    if (type === 'quote_request') {
        const quoteStatusMap: Record<string, { label: string; color: string; icon: any }> = {
            'pending': { label: 'Cotización Pendiente', color: '#F59E0B', icon: FaHourglass },
            'sent': { label: 'Cotización Enviada', color: '#3B82F6', icon: FaCheckCircle },
            'accepted': { label: 'Cotización Aceptada', color: '#10B981', icon: FaCheckDouble },
            'declined': { label: 'Cotización Rechazada', color: '#6B7280', icon: FaBan },
            'expired': { label: 'Cotización Expirada', color: '#6B7280', icon: FaTimesCircle },
            'cancelled': { label: 'Cotización Cancelada', color: '#EF4444', icon: FaTimesCircle },
        }
        return quoteStatusMap[status] || { label: status, color: '#6B7280', icon: FaHourglass }
    }
    
    // Service request statuses
    const statusMap: Record<string, { label: string; color: string; icon: any }> = {
        'slot_blocked': { label: 'Esperando Aprobación del Proveedor', color: '#F59E0B', icon: FaHourglass },
        'pending': { label: 'Pendiente', color: '#F59E0B', icon: FaHourglass },
        'approved': { label: 'Aprobada - Esperando Pago', color: '#3B82F6', icon: FaCheckCircle },
        'accepted': { label: 'Aceptada', color: '#3B82F6', icon: FaCheckCircle },
        'payment_submitted': { label: 'Pago Enviado - Esperando Verificación', color: '#8B5CF6', icon: FaCreditCard },
        'confirmed': { label: 'Confirmada', color: '#10B981', icon: FaCheckDouble },
        'in_progress': { label: 'En Progreso', color: '#8B5CF6', icon: FaTools },
        'completed': { label: 'Completada', color: '#10B981', icon: FaCheckDouble },
        'finished': { label: 'Finalizada', color: '#10B981', icon: FaCheckDouble },
        'cancelled_by_provider': { label: 'Cancelada por Proveedor', color: '#EF4444', icon: FaTimesCircle },
        'cancelled_by_client': { label: 'Cancelada por Ti', color: '#EF4444', icon: FaTimesCircle },
        'cancelled': { label: 'Cancelada', color: '#EF4444', icon: FaTimesCircle },
        'denied_by_provider': { label: 'Rechazada por Proveedor', color: '#6B7280', icon: FaBan },
        'rejected': { label: 'Rechazada', color: '#6B7280', icon: FaBan },
    }
    return statusMap[status] || { label: status, color: '#6B7280', icon: FaHourglass }
}

const RequestCard = ({ request }: RequestCardProps) => {
    const navigate = useNavigate()
    const statusInfo = getStatusInfo(request.status, request.type)
    const location = request.location_details?.city || request.location_details?.address

    const StatusIcon = statusInfo.icon
    
    const handleClick = () => {
        if (request.type === 'quote_request') {
            navigate(`/quote/${request.id}`)
        } else {
            navigate(`/request/${request.id}`)
        }
    }

    return (
        <div 
            className="request-card"
            onClick={handleClick}
            style={{ cursor: 'pointer' }}
        >
            {/* Status Banner */}
            <div 
                className="request-card__status-banner"
                style={{ backgroundColor: `${statusInfo.color}20` }}
            >
                <StatusIcon className="request-card__status-icon" style={{ color: statusInfo.color }} />
                <span className="request-card__status-text" style={{ color: statusInfo.color }}>
                    {statusInfo.label}
                </span>
            </div>

            <div className="request-card__header">
                <div className="request-card__service-icon">
                    <FaTools />
                </div>
                <h3 className="request-card__title">{request.service_name}</h3>
            </div>

            {request.description && (
                <p className="request-card__description">
                    {request.description.length > 100 
                        ? `${request.description.slice(0, 100)}...` 
                        : request.description}
                </p>
            )}

            <div className="request-card__details">
                {request.estimated_price && (
                    <div className="request-card__detail">
                        <FaDollarSign className="request-card__icon" />
                        <span>
                            {request.type === 'quote_request' && request.status === 'pending' 
                                ? 'Cotización pendiente' 
                                : `S/.${Number(request.estimated_price).toFixed(2)}`}
                        </span>
                    </div>
                )}
                
                {request.scheduled_date && (
                    <div className="request-card__detail">
                        <FaClock className="request-card__icon" />
                        <span>
                            {(() => {
                                // Parse date string as local date to avoid timezone shift
                                // If it's already a date string in YYYY-MM-DD format, parse it as local
                                const dateStr = request.scheduled_date
                                let date: Date
                                if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
                                    // Parse as local date (YYYY-MM-DD)
                                    const [year, month, day] = dateStr.split('-').map(Number)
                                    date = new Date(year, month - 1, day)
                                } else {
                                    // Fallback to standard parsing
                                    date = new Date(dateStr)
                                }
                                return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })
                            })()}
                            {request.scheduled_hour !== undefined && request.scheduled_hour !== null && (
                                <> - {String(request.scheduled_hour).padStart(2, '0')}:00
                                {request.estimated_duration_hours && (
                                    <> ({request.estimated_duration_hours}h)</>
                                )}
                                </>
                            )}
                        </span>
                    </div>
                )}
                
                {location && (
                    <div className="request-card__detail">
                        <FaMapMarkerAlt className="request-card__icon" />
                        <span>{location}</span>
                    </div>
                )}
            </div>
        </div>
    )
}

export default RequestCard
